import React, { Component, Fragment } from 'react';
import { Query } from '../../reactApolloCompat';
import withStyles from '@mui/styles/withStyles';
import ColumnLayout from '../../components/layout/ColumnLayout';
import CremationOrderFragment from '../../fragments/CremationOrder';
import { getUser } from '../../utils/sessions';
import CommunicationNoteModal from '../CommunicationNotes/CommunicationNoteModal';
import { getOffice } from '../../environment';
import Grid from '../../components/layout/Grid';
import ReadonlyField from '../../components/form/ReadonlyField';
import Typography from '../../components/form/Typography';
import { niceDateFromString, niceDateTimeFromString, niceTimeFromString } from '../../utils/date';
import Separator from '../../components/layout/Separator';
import { EnquiriesIcon } from '../../components/IconIndex';
import Button from '../../components/form/Button';
import { PAGE_ENQUIRIES } from '../../utils/strings';
import AppointmentModal from '../../components/modal/AppointmentModal';
import Table, { Cell, HeaderRow, Row } from '../../components/table/Table';
import { appointmentColumns } from '../../utils/appointments';
import gql from 'graphql-tag';
import Appointment from '../../fragments/Appointment';
import Spinner from '../../components/loading/Spinner';
import AlertBar from '../../components/form/AlertBar';

class CorrespondenceTab extends Component {
    state = {
        showAppointmentModal: false, 
        appointmentId: null 
    }

    render() {
        return (
            <Fragment>
                <ColumnLayout>
                    {this.renderLeftColumn()}
                    {this.renderRightColumn()}
                </ColumnLayout>
            </Fragment>
        );
    }

    renderLeftColumn() {
        return (
            <Grid container>
                <Grid item>
                    {this.renderCommunicationNotes()}
                </Grid>

                <Grid item>
                    {this.renderAppointments()}
                </Grid>
                
                <Grid item>
                    {this.renderRelatedRecords()}
                </Grid>
            </Grid>
        );
    }

    renderRightColumn() {
        return <Fragment>{this.renderCremationDateHistories()}</Fragment>;
    }

    readAppointments() {
        return gql`
            ${Appointment}
            query readAppointments($cremationID: ID){
                readAppointments(cremationID: $cremationID){
                    edges {
                        node {
                            ...Appointment
                        }
                    }
                }
            }
        `
    }

    onClickViewAppointment(id) {
        this.setState({ showAppointmentModal: true, appointmentId: id });
    }

    renderAppointments() {
        const { form } = this.props;
        const { showAppointmentModal, appointmentId } = this.state;
        const enquiryID = Number(form.getField('ID'));

        return (
            <Grid container bucket>
                <Grid item>
                    <h4>Appointments</h4>
                </Grid>

                <AppointmentModal 
                    open={!!showAppointmentModal}
                    parentForm={form}
                    appointmentId={appointmentId || null}
                    onClose={() => this.setState({ showAppointmentModal: false, appointmentId: null })}
                />

                <Grid item>
                    {!!enquiryID && 
                        <Query
                            query={this.readAppointments()}
                            variables={{ cremationID: enquiryID }}
                            fetchPolicy="cache-and-network"
                            notifyOnNetworkStatusChange
                        >
                            {({ loading, data, error, refetch, networkStatus }) => {

                                if (!!loading && (!data || networkStatus === 1))
                                    return (
                                        <Grid item>
                                            <Spinner /> Loading appointments...
                                        </Grid>
                                    );
                                if (!!error)
                                    return (
                                        <Grid item>
                                            <AlertBar variant="error">Error loading appointments.</AlertBar>
                                        </Grid>
                                    );

                                const items = (data && data.readAppointments && data.readAppointments.edges.map(e => e.node)) || [];
                            
                                return items && items.length > 0 ? (
                                    <Table>
                                        <HeaderRow pad>
                                            {appointmentColumns.map(column => (
                                                <Cell key={column.label}>{column.label}</Cell>
                                            ))}
                                        </HeaderRow>
                                        {items && items.length > 0 && items.map((row, idx) => {
                                            return (
                                                <Row pad key={row.ID}>
                                                    {appointmentColumns.map(column => {
                                                        return (
                                                            <Cell
                                                                key={column.label}
                                                                dataLabel={column.label}
                                                            >
                                                                {column.renderCell(row, this, idx)}
                                                            </Cell>
                                                        );
                                                    })}
                                                </Row>
                                            )
                                        })}
                                    </Table>
                                ) : <p>There are currently no appointments.</p>
                            }}
                        </Query>
                    }
                </Grid>
            </Grid>
        ) 
    }

    renderRelatedRecords() {
        const { form, context } = this.props;
        const hasRelatedEnquiry = !!form.getField('Enquiry.ID');
        const enquiryLegacyKey = hasRelatedEnquiry ? form.getField('Enquiry.LegacyKey') : null;
        const enquiryType = hasRelatedEnquiry ? form.getField('Enquiry.EnquiryType') : null;
        const enquiryID = hasRelatedEnquiry ? form.getField('Enquiry.ID') : null;
        const relatedRecordListLink = enquiryType ?  PAGE_ENQUIRIES : null; 

        return (
            <Grid container bucket>
                <Grid item>
                    <h4>Related Records</h4>
                </Grid>

                {hasRelatedEnquiry && enquiryLegacyKey && relatedRecordListLink && enquiryID ? (
                    <Grid item>
                        <Button
                            icon={<EnquiriesIcon />}
                            variant="urgent"
                            onClick={() => context.props.history.push(relatedRecordListLink + enquiryID)}
                        >
                            View Booking{' '}{enquiryLegacyKey}
                        </Button>
                    </Grid>
                ) : (<Grid item><p>There is no related booking record.</p></Grid>)}
            </Grid>
        )
    }

    renderCommunicationNotes() {
        const { form } = this.props;
        const user = getUser();
        const office = getOffice();
        return <CommunicationNoteModal
            recordID={form.getField('ID')}
            recordKey={form.getField('LegacyKey')}
            recordType="CremationOrder"
            substitutionFields={{
                '[[Client.FirstName]]': form.getField('Client.FirstName'),
                '[[Client.Surname]]': form.getField('Client.Surname'),
                '[[Deceased.FirstName]]': form.getField('Deceased.FirstName'),
                '[[Deceased.Surname]]': form.getField('Deceased.Surname'),
                '[[Reference]]': form.getField('LegacyKey'),
                '[[CremationNumber]]': form.getField('CremationNumber'),
                '[[StaffName]]': user.FirstName + ' ' + user.Surname,
                '[[OfficeName]]': office && office.Title,
                '[[OfficePhone]]': office && office.Phone,
                '[[OfficeReview]]': null,
                '[[Business.Name]]': form.getField('Business.Name'),
                '[[ScheduledInsertionDate]]': form.getField('EstimatedInsertionDate'),
                '[[ScheduledInsertionWindow]]': null
            }}
        />;
    }

    renderCremationDateHistories() {
        const { form } = this.props;
        const cremationDateHistories = form.getState('CremationDateHistories') || [];
        if (cremationDateHistories.length < 1) return null;

        return (
            <Grid container bucket>
                <Grid item>
                    <h4>Booking Request History</h4>
                </Grid>
                <Grid item>
                    <ReadonlyField>
                        {cremationDateHistories
                            .filter(e => !!e)
                            .sort((a, b) => (a.Created > b.Created ? -1 : 1))
                            .map((cremationDateHistory, idx) => (
                                <Fragment key={cremationDateHistory.ID}>
                                    {!!idx && <Separator />}
                                    <Typography>
                                        {!!idx ? 'Was' : 'Current'} Requested
                                        Arrival: {niceDateFromString(cremationDateHistory.Date)}
                                        {!!cremationDateHistory.Time &&
                                            ' at ' + niceTimeFromString(cremationDateHistory.Time)}
                                    </Typography>
                                    <Typography weight="light">
                                        Updated on {niceDateTimeFromString(cremationDateHistory.Created)}
                                        {!!cremationDateHistory.Author && ' by ' + cremationDateHistory.Author}
                                    </Typography>
                                </Fragment>
                            ))}
                    </ReadonlyField>
                </Grid>
            </Grid>
        );
    }
}

export default {
    id: 'Correspondence',
    label: 'Correspondence',
    component: withStyles({})(CorrespondenceTab),
    fragment: CremationOrderFragment
};
